import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createAccountServiceClient, type AccountLifecycle } from "./client";

export type AccountLifecyclePhase = "idle" | "loading" | "ready" | "working" | "error";

// Reads and drives the account-deletion grace-period lifecycle (GET/POST/DELETE /v1/me/account).
// Passwords and MFA are not part of this -- Cognito owns those (see useCognitoSecurity).
export function useAccountLifecycle() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.accountService?.apiBaseUrl;

  const [lifecycle, setLifecycle] = useState<AccountLifecycle | null>(null);
  const [phase, setPhase] = useState<AccountLifecyclePhase>("idle");
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }
    return createAccountServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setPhase("error");
        setError("Account management is not available right now.");
        return;
      }
      setPhase("loading");
      setError(null);
      try {
        const next = await client.getAccountLifecycle(signal);
        if (signal?.aborted) {
          return;
        }
        setLifecycle(next);
        setPhase("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }
        setError(nextError instanceof Error ? nextError.message : "We could not load your account status.");
        setPhase("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [auth.isAuthenticated, refresh]);

  const requestDeletion = useCallback(async () => {
    if (!client) {
      return false;
    }
    setPhase("working");
    setError(null);
    try {
      setLifecycle(await client.requestAccountDeletion());
      setPhase("ready");
      return true;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "We could not schedule your account deletion.");
      setPhase("error");
      return false;
    }
  }, [client]);

  const cancelDeletion = useCallback(async () => {
    if (!client) {
      return false;
    }
    setPhase("working");
    setError(null);
    try {
      setLifecycle(await client.cancelAccountDeletion());
      setPhase("ready");
      return true;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "We could not restore your account.");
      setPhase("error");
      return false;
    }
  }, [client]);

  return { lifecycle, phase, error, refresh, requestDeletion, cancelDeletion };
}
